<template>
  <application-layout>
    <v-container>
      <h1 class="mb-2">Manage Dispositions</h1>
      <v-layout class="mb-3">
        <create
          v-model="callTypeModal"
          @collapse="clearCallType"
          :submitted="submitted"
        >
          <v-btn
            data-testid="add-new-call-type-btn"
            class="add-new-call-type-btn primary mr-3 ml-0"
            slot="modal-trigger"
            @click.stop="callTypeModal = true"
          >
            <v-icon>add</v-icon>
            Add New Call Type
          </v-btn>

          <div slot="label">Call Type</div>

          <template slot="modal-body">
            <v-flex>
              <v-text-field
                data-testid="enter-new-call-type-name-input"
                class="enter-new-call-type-name-input"
                filled
                required
                :rules="inputRules"
                v-model="callType"
                label="Enter call type..."
              />
            </v-flex>
            <v-flex>
              <v-select
                data-testid="new-applicable-roles-menu"
                class="new-applicable-roles-menu"
                filled
                required
                v-model="applicableRoles"
                :items="availableRoles"
                multiple
                label="Select applicable roles..."
              />
            </v-flex>
          </template>

          <v-btn
            data-testid="create-call-type-btn"
            class="create-call-type-btn"
            slot="create-button"
            color="primary"
            :loading="loading"
            @click="createCallType"
            :disabled="!callType"
          >
            Create Call Type
          </v-btn>
        </create>

        <create
          v-model="dispositionModal"
          @collapse="clearDisposition"
          :submitted="submitted"
        >
          <v-btn
            data-testid="add-new-disposition-btn"
            class="add-new-disposition-btn primary mr-3 ml-0"
            slot="modal-trigger"
            @click.stop="dispositionModal = true"
          >
            <v-icon>add</v-icon>
            Add New Disposition
          </v-btn>

          <div slot="label">Disposition</div>

          <template slot="modal-body">
            <v-text-field
              data-testid="enter-new-disposition-name-input"
              class="enter-new-disposition-name-input"
              v-model="disposition"
              filled
              label="Enter disposition..."
              required
              :rules="inputRules"
            />
            <v-select
              v-if="callTypes.length"
              data-testid="disposition-call-type-menu"
              class="disposition-call-type-menu"
              v-model="dispositionCallType"
              filled
              label="Select disposition's call type..."
              :items="callTypes"
              :rules="inputRules"
              item-text="name"
              return-object
            />
          </template>

          <v-btn
            data-testid="create-disposition-btn"
            class="create-disposition-btn"
            slot="create-button"
            color="primary"
            :loading="loading"
            @click="createDisposition"
            :disabled="!disposition || !dispositionCallType"
          >
            Create Disposition
          </v-btn>
        </create>
      </v-layout>

      <v-flex class="mb-5">
        <div class="text-h5 font-weight-bold mb-2 mt-4">Call Types</div>
        <v-data-table
          :headers="callTypeHeaders"
          :items="callTypes"
          no-data-text="There are no call types to display"
        >
          <template slot="item" slot-scope="props">
            <tr>
            <td>{{ props.item.id }}</td>
            <td>{{ props.item.name }}</td>
            <td>{{ formatDateAndTime(props.item.created_at) }}</td>
            <td>{{ formatDateAndTime(props.item.updated_at) }}</td>
            <td>
              <v-btn
                data-testid="edit-call-type-btn"
                class="edit-call-type-btn primary"
                small
                @click="openEdit(props.item)"
              >
                Edit
              </v-btn>
            </td>
            </tr>
          </template>
        </v-data-table>
      </v-flex>

      <div>
        <v-layout wrap>
          <v-flex xs9>
            <div class="text-h5 font-weight-bold mb-2">
              Dispositions
            </div>
          </v-flex>
          <v-flex xs3>
            <v-switch
              data-testid="toggle-dispositions-switch"
              class="toggle-dispositions-switch"
              :class="switchClass"
              v-model="toggleDispositionSwitch"
              :label="toggleDispositionSwitch ? 'Display Only Active Dispositions' : 'Display All Dispositions'"
              @change="handleToggledDispositions"
            />
          </v-flex>
        </v-layout>
        <v-data-table
          :headers="dispositionHeaders"
          :items="mappedDispositions"
          no-data-text="There are no dispositions to display"
        >
          <template slot="item" slot-scope="props">
            <tr>
            <td>{{ props.item.id }}</td>
            <td>{{ props.item.name }}</td>
            <td>{{ props.item.call_type }}</td>
            <td>{{ formatDateAndTime(props.item.created_at) }}</td>
            <td>{{ formatDateAndTime(props.item.updated_at) }}</td>
            <td>
              <v-btn
                data-testid="edit-disposition-btn"
                class="edit-disposition-btn primary mr-2"
                small
                @click="openEdit(props.item)"
              >
                Edit
              </v-btn>
              <v-btn
                data-testid="disable-activate-disposition-btn"
                class="disable-activate-disposition-btn mx-2"
                small
                :class="props.item.enabled ? 'error' : 'success'"
                @click="openDispositionStatusModal(props.item)"
              >
                {{ props.item.enabled ? 'Disable' : 'Activate' }}
              </v-btn>
            </td>
            </tr>
          </template>
        </v-data-table>
      </div>

      <edit
          @updateCallType="updateCallType"
          @updateDisposition="updateDisposition"
          v-model="editModal"
          :editObj="editObj"
          :callTypes="callTypes"
          :inputRules="inputRules"
          :availableRoles="availableRoles"
          :assignedRoles="assignedRoles"
      />

      <application-modal
        v-if="selectedDisposition"
        v-model="dispositionStatusModal"
        :width="'600'"
        @cancel="closeDispositionStatusModal"
        @collapse="closeDispositionStatusModal"
      >
        <div slot="modal-title">
          <v-layout align-center>{{ selectedDisposition.enabled ? 'Disable' : 'Activate' }} Disposition</v-layout>
        </div>
        <div slot="modal-body">
          <h3 class="text-h6">
            <v-icon color="orange">warning</v-icon>
            Are you sure you want to {{ selectedDisposition.enabled ? 'disable' : 'activate' }} this disposition?
          </h3>
          <div>
            <v-flex class="my-3">
              <h3 class="font-weight-regular">Disposition Type: {{ selectedDisposition.name }}</h3>
            </v-flex>
            <v-flex>
              <h3 class="font-weight-regular">Call Type: {{ selectedDisposition.call_type }}</h3>
            </v-flex>
          </div>
        </div>
        <v-btn
          data-testid="confirm-disable-activate-disposition-btn"
          class="confirm-disable-activate-disposition-btn"
          slot="create-button"
          color="primary white--text"
          @click="handleUpdateDisposition"
          :loading="loading"
        >
          {{ selectedDisposition.enabled ? 'Disable' : 'Activate' }}
        </v-btn>
      </application-modal>
    </v-container>
  </application-layout>
</template>

<script>
import { callTypeHeaders, dispositionHeaders } from "@/helpers/dispositions/call-type-disposition-headers";
import ApplicationLayout from "@/layouts/ApplicationLayout";
import ApplicationModal from "@/components/ApplicationModal";
import Create from "@/views/dispositions/partials/Create";
import Edit from "@/views/dispositions/partials/Edit";
import { dateMethodMixins } from "@/mixins/dateMethodMixins";
import { inputRules } from "@/helpers/form-rules";
import { mapState } from "vuex";

export default {
  mixins: [dateMethodMixins],

  data() {
    return {
      inputRules: inputRules,

      dispositionModal: false,
      callTypeModal: false,
      editModal: false,

      availableRoles: [],
      assignedRoles: [],

      editObj: "",
      callType: "",
      callTypes: [],
      applicableRoles: [],
      disposition: "",
      dispositionCallType: "",
      dispositions: [],

      loading: false,
      submitted: false,
      callTypeHeaders: callTypeHeaders,
      dispositionHeaders: dispositionHeaders,

      dispositionStatusModal: false,
      selectedDisposition: null,
      toggleDispositionSwitch: false
    };
  },

  methods: {
    clearCallType() {
      this.callType = "";
    },

    clearDisposition() {
      this.disposition = "";
      this.dispositionCallType = "";
    },

    openEdit(item) {
      this.editModal = true;
      this.editObj = item;

      if (item.roles && item.roles.length) {
        item.roles.forEach(role => {
          this.assignedRoles.push(role.id);
        });
      }
    },

    openDispositionStatusModal(item) {
      this.dispositionStatusModal = true;
      this.selectedDisposition = item;
    },

    handleToggledDispositions() {
      this.getDispositions();
    },

    async handleUpdateDisposition() {
      this.loading = true;
      const payload = {
        ...this.selectedDisposition,
        enabled: this.selectedDisposition.enabled === 1 ? 0 : 1
      };
      await this.$store.dispatch("updateDisposition", payload);
      this.loading = false;
      this.closeDispositionStatusModal();
      this.getDispositions();
    },

    closeDispositionStatusModal() {
      this.dispositionStatusModal = false;
      this.selectedDisposition = null;
    },

    getCallTypes() {
      this.$store.dispatch("getAllCallTypes")
        .then(resp => {
          this.callTypes = resp.data.call_types;
        });
    },

    getDispositions() {
      const status = this.toggleDispositionSwitch ? 0 : 1;
      this.$store.dispatch("getAllDispositions", status)
        .then((resp) => {
          this.dispositions = resp.data.dispositions;
        });
    },

    createCallType() {
      this.submitted = false;
      this.loading = true;
      let callType = { name: this.callType, roles: this.applicableRoles };

      this.$store.dispatch("submitCallType", callType)
        .then(() => {
          this.submitted = true;
          this.callTypeModal = false;
          this.loading = false;
          this.clearCallType();
          this.getCallTypes();
        })
        .catch(() => {
          this.loading = false;
        });
    },

    updateCallType() {
      this.loading = true;

      this.$store.dispatch("updateCallType", this.editObj)
        .then(() => {
          this.editModal = false;
          this.loading = false;
          this.getCallTypes();
        })
        .catch(() => {
          this.loading = false;
        });
    },

    createDisposition() {
      this.submitted = false;
      this.loading = true;

      let disposition = {
        name: this.disposition,
        call_type_id: this.dispositionCallType.id
      };

      this.$store.dispatch("submitDisposition", disposition)
        .then(() => {
          this.submitted = true;
          this.dispositionModal = false;
          this.loading = false;
          this.getDispositions();
          this.clearDisposition();
        })
        .catch(() => {
          this.loading = false;
        });
    },

    updateDisposition() {
      this.loading = true;

      this.$store.dispatch("updateDisposition", this.editObj)
        .then(() => {
          this.loading = false;
          this.editModal = false;
          this.getDispositions();
        })
        .catch(() => {
          this.loading = false;
        });
    },

    getCallCenterRoles() {
      this.$store.dispatch("getCallCenterRoles")
        .then(resp => {
          if (resp && resp.data && resp.data.roles.length > 0) {
            resp.data.roles.forEach(role => {
              this.availableRoles.push({
                value: role.id,
                text: role.name
              });
            });
          }
        });
    }
  },

  computed: {
    ...mapState({
      drawer: state => state.application.settings.drawer
    }),
    mappedDispositions() {
      let nameFilter = (array, id) => array.filter(item => item.id === id)[0]["name"];
      if (this.callTypes.length) {
        return this.dispositions.map(disposition => {
          return { ...disposition, call_type: nameFilter(this.callTypes, disposition.call_type_id) };
        });
      } else {
        return [];
      };
    },

    switchClass() {
      const breakpointXL = this.$vuetify.breakpoint.name === "xl";
      if (!breakpointXL) {
        return "ml-0";
      } else if (breakpointXL && this.drawer) {
        return "switch-breakpoint-nav";
      }
      return "switch-breakpoint-xl";
    }
  },

  created() {
    this.getCallTypes();
    this.getDispositions();
    this.getCallCenterRoles();
  },

  components: {
    ApplicationLayout,
    ApplicationModal,
    Create,
    Edit
  }
};
</script>

<style scoped>
  .switch-breakpoint-xl {
    margin-left: 11rem;
  }
  .switch-breakpoint-nav {
    margin-left: 7rem;
  }
</style>
